class ProductMultiUploader {
  constructor(options = {}) {
    this.config = {
      maxFiles: options.maxFiles || 100, // Default to 100 (effectively unlimited)
      enableCrop: options.enableCrop !== false,
      faceCropMarginFactor: 0.4
    };

    this.files = [];
    this.processed = []; // { originalFile, processedFile, previewUrl, azureUrl }
    this.container = options.containerEl || document.getElementById('product-multi-uploader');
    if (!this.container) return;

    this.uploadSection = this.container.querySelector('#product-multi-upload-zone');
    this.fileInput = this.container.querySelector('#product-multi-upload');
    this.previewGrid = this.container.querySelector('#multi-photo-preview');
    this.kidNameField = this.container.querySelector('#kid-name-field');
    this.kidNameInput = this.container.querySelector('#kid-name-input');
    this.kidAgeInput = this.container.querySelector('#kid-age');
    this.formEl = this.container.closest('form[data-type="add-to-cart-form"]') || document.querySelector('form[data-type="add-to-cart-form"]');

    // COMMENTED OUT - Face API (Will use it later)
    // this.faceApiLoaded = false;

    this.skipGlobal = options.skipGlobal === true;

    this.bindEvents();
    this.setupLanguageValidation();
    this.attachFormGuards = this.attachFormGuards.bind(this);
    this.updateButtonsState = this.updateButtonsState.bind(this);
    this.attachFormGuards();
    this.updateButtonsState();
    this.observeFormMutations();
    // Re-run after a short delay to catch late-rendered buttons
    setTimeout(() => this.updateButtonsState(), 100);
    setTimeout(() => this.updateButtonsState(), 500);
    setTimeout(() => this.updateButtonsState(), 1500);
    // Global capture guards as last line of defense
    this.isAdding = false;
    this.suppressNextClick = false;
    if (!this.skipGlobal) {
      this.installGlobalGuards();
      this.installActionTracking();
    }
    // Listen for successful add-to-cart (single product only) to show upsell popup
    this.installAddSuccessListener();
    // Ensure dynamic checkout inside shadow DOM is guarded
    this.installPaymentShadowGuards();
  }

  bindEvents() {
    if (this.uploadSection) {
      // Make entire dropzone clickable
      this.uploadSection.setAttribute('role', 'button');
      this.uploadSection.setAttribute('tabindex', '0');
      this.uploadSection.addEventListener('click', (e) => {
        // Avoid overlapping with the inner button
        if (e.target && e.target.closest('.upload-btn')) {
          return;
        }
        if (this.fileInput) this.fileInput.click();
      });
      this.uploadSection.addEventListener('keydown', (e) => {
        // Avoid overlapping when focus is on the inner button
        if (e.target && e.target.closest('.upload-btn')) return;
        if ((e.key === 'Enter' || e.key === ' ') && this.fileInput) {
          e.preventDefault();
          this.fileInput.click();
        }
      });
      // Explicit handler for the inner button to stop propagation
      const innerButton = this.uploadSection.querySelector('.upload-btn');
      if (innerButton) {
        innerButton.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this.fileInput) this.fileInput.click();
        });
        innerButton.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            // Prevent bubbling to section handler
            e.stopPropagation();
          }
        });
      }

      this.uploadSection.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.uploadSection.classList.add('drag-over');
      });
      this.uploadSection.addEventListener('dragenter', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.uploadSection.classList.add('drag-over');
      });
      this.uploadSection.addEventListener('dragleave', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!this.uploadSection.contains(e.relatedTarget)) {
          this.uploadSection.classList.remove('drag-over');
        }
      });
      this.uploadSection.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.uploadSection.classList.remove('drag-over');
        const files = Array.from(e.dataTransfer.files || []);
        this.handleFiles(files);
      });
    }

    if (this.fileInput) {
      this.fileInput.addEventListener('change', (e) => {
        const files = Array.from(e.target.files || []);
        this.handleFiles(files);
      });
    }
  }

  async handleFiles(incomingFiles) {
    try {
      // Show analyzing immediately upon selection
      this.showLoadingState('Our AI Model is Analyzing Your photos...');

      const remainingSlots = this.config.maxFiles - this.processed.length;
      if (remainingSlots <= 0) {
        this.showToaster(`Maximum ${this.config.maxFiles} images reached`, 'warning');
        this.hideLoadingState();
        return;
      }

      const imageFiles = incomingFiles
        .filter(f => f && f.type && f.type.startsWith('image/'));

      if (imageFiles.length === 0) {
        this.showToaster('Please select valid image files.', 'error');
        this.hideLoadingState();
        return;
      }

      // Check file sizes first
      const validFiles = [];
      for (const file of imageFiles) {
        if (file.size > 20 * 1024 * 1024) {
          this.showToaster('Please select an image smaller than 20MB.', 'error');
          continue;
        }
        validFiles.push(file);
      }

      if (validFiles.length === 0) {
        this.hideLoadingState();
        return;
      }

      // COMMENTED OUT - Face API (Will use it later)
      // if (this.config.enableCrop && !this.faceApiLoaded) {
      //   // Keep the analyzing message visible while models load
      //   await this.ensureFaceApi();
      // }

      // Process files sequentially until we get the required number of successful images
      let processedCount = 0;
      const errorMessages = [];
      const successfulImages = [];

      for (const file of validFiles) {
        if (successfulImages.length >= remainingSlots) {
          break; // Stop processing once we have enough successful images
        }

        try {
          processedCount++;
          const result = await this.processSingleFile(file);
          if (result) {
            successfulImages.push(result);
          }
        } catch (err) {
          console.error('❌ File processing failed:', err);
          const errorMsg = err?.message || 'Failed to process image';
          if (!errorMessages.includes(errorMsg)) {
            errorMessages.push(errorMsg);
          }
        }
      }

      // Add new successful images to processed array
      successfulImages.forEach(image => {
        this.processed.push(image);
      });

      // Re-render all images
      this.reRenderAllImages();

      const successCount = successfulImages.length;

      // Show individual error messages (but limit to avoid spam)
      const maxErrorsToShow = 3;
      errorMessages.slice(0, maxErrorsToShow).forEach(msg => {
        this.showToaster(msg, 'error');
      });

      if (errorMessages.length > maxErrorsToShow) {
        this.showToaster(`${errorMessages.length - maxErrorsToShow} more images failed validation`, 'warning');
      }

      await this.syncHiddenInputs();
      this.updateButtonsState();

      // Show summary message
      const failureCount = processedCount - successCount;
      const skippedCount = validFiles.length - processedCount;

      if (successCount > 0) {
        let message = `${successCount} photo${successCount > 1 ? 's' : ''} uploaded successfully!`;
        if (skippedCount > 0) {
          message += ` ${skippedCount} additional image${skippedCount > 1 ? 's were' : ' was'} skipped.`;
        }
        this.showToaster(message, 'success');
      }

    } catch (err) {
      console.error('❌ Multi upload failed:', err);
      this.showToaster('An error occurred while processing images.', 'error');
    } finally {
      this.hideLoadingState();
    }
  }

  async processSingleFile(file) {
    try {
      // COMMENTED OUT - Face API (Will use it later)
      // // Always ensure face-api for validation (reject multi/no-face images)
      // if (!this.faceApiLoaded) {
      //   await this.ensureFaceApi();
      // }

      let finalFile = file;
      // let didCrop = false; // Unused when Face API is disabled

      // COMMENTED OUT - Face API validation and cropping (Will use it later)
      // // Validate strictly; throws for 0 or multiple faces
      // const imgEl = await this.createImageElement(file);
      // const validation = await this.validateFace(imgEl);

      // // Optional crop if enabled and we have a faceBox
      // if (this.config.enableCrop && validation.faceBox) {
      //   try {
      //     const croppedBlob = await this.cropImageAroundFace(
      //       imgEl,
      //       validation.faceBox,
      //       this.config.faceCropMarginFactor
      //     );
      //     finalFile = new File([croppedBlob], file.name.replace(/\.[^.]+$/, '.png'), {
      //       type: 'image/png',
      //       lastModified: Date.now()
      //     });
      //     didCrop = true;
      //   } catch (cropErr) {
      //     console.warn('⚠️ Face cropping failed, continuing with original image:', cropErr);
      //   }
      // }

      const previewUrl = URL.createObjectURL(finalFile);
      const azureUrl = await this.uploadImageToAzure(finalFile);

      return {
        originalFile: file,
        processedFile: finalFile,
        previewUrl,
        azureUrl
      };
    } catch (err) {
      console.error('❌ File processing failed:', err);
      const message = (err && err.message) ? err.message : 'Failed to process one of the images.';
      // Don't show individual error toasters here since we handle them in handleFiles
      throw new Error(message);
    }
  }

  renderPreviewItem(item) {
    if (!this.previewGrid) return;
    const idx = this.processed.length;
    const wrapper = document.createElement('div');
    wrapper.className = 'photo-preview multi-item';
    wrapper.setAttribute('data-image-url', item.azureUrl || item.previewUrl || '');

    wrapper.innerHTML = `
      <div class="preview-image-container">
        <div class="preview-image-full" style="position:relative;">
          <img src="${item.previewUrl}" alt="Uploaded photo ${idx}"/>
          <button type="button" class="remove-photo" aria-label="Remove photo"><i class="fas fa-times"></i></button>
        </div>
      </div>
    `;

    const removeBtn = wrapper.querySelector('.remove-photo');
    removeBtn.addEventListener('click', async () => {
      this.processed = this.processed.filter(p => p !== item);
      // Re-render all images to maintain proper order and indices
      this.reRenderAllImages();
      await this.syncHiddenInputs();
      if (this.kidNameField && this.processed.length === 0) {
        this.kidNameField.style.display = 'none';
      }
      // Show requirements again if no images remain
      try {
        if (this.processed.length === 0) {
          const req = this.container.querySelector('.photo-requirements-visual');
          if (req) req.style.display = '';
          const reqHeader = this.container.querySelector('.photo-requirements-header');
          if (reqHeader) reqHeader.style.display = '';
        }
      } catch { }
      this.updateButtonsState();
    });

    this.previewGrid.appendChild(wrapper);

    // Show kid name field once first valid image added
    if (this.kidNameField && this.processed.length === 1) {
      this.kidNameField.style.display = 'block';
    }
    // Hide requirements once an image is added
    try {
      const req = this.container.querySelector('.photo-requirements-visual');
      if (req) req.style.display = 'none';
      const reqHeader = this.container.querySelector('.photo-requirements-header');
      if (reqHeader) reqHeader.style.display = 'none';
    } catch { }
    this.updateButtonsState();
  }

  reRenderAllImages() {
    if (!this.previewGrid) return;

    // Clear existing preview items
    const existingItems = this.previewGrid.querySelectorAll('.photo-preview.multi-item');
    existingItems.forEach(item => item.remove());

    // Re-render all images in their current order (already sorted by score)
    this.processed.forEach(item => {
      this.renderPreviewItem(item);
    });

    // Hide/show requirements visual based on image count
    try {
      const req = this.container.querySelector('.photo-requirements-visual');
      if (req) {
        req.style.display = this.processed.length > 0 ? 'none' : '';
      }
    } catch { }
  }

  async syncHiddenInputs() { this.syncHiddenInputsSync(); }

  syncHiddenInputsSync() {
    try {
      if (!this.formEl) return;
      // Remove old inputs
      Array.from(this.formEl.querySelectorAll('input[name^="properties[Photo "]')).forEach(el => el.remove());
      // Also remove any previous bundle-scoped kid names and ages
      Array.from(this.formEl.querySelectorAll('input[name="properties[Kid Name]"]')).forEach(el => el.remove());
      Array.from(this.formEl.querySelectorAll('input[name="properties[Kid Age]"]')).forEach(el => el.remove());

      const existingKid = this.formEl.querySelector('input[name="properties[Kid Name]"]');
      if (!existingKid && this.kidNameInput && this.kidNameInput.value) {
        const kid = document.createElement('input');
        kid.type = 'hidden';
        kid.name = 'properties[Kid Name]';
        kid.value = this.kidNameInput.value;
        this.formEl.appendChild(kid);
      } else if (existingKid && this.kidNameInput) {
        existingKid.value = this.kidNameInput.value || '';
      }

      // Add Kid Age
      const existingAge = this.formEl.querySelector('input[name="properties[Kid Age]"]');
      if (!existingAge && this.kidAgeInput && this.kidAgeInput.value) {
        const age = document.createElement('input');
        age.type = 'hidden';
        age.name = 'properties[Kid Age]';
        age.value = this.kidAgeInput.value;
        this.formEl.appendChild(age);
      } else if (existingAge && this.kidAgeInput) {
        existingAge.value = this.kidAgeInput.value || '';
      }

      // If bundle is open, capture properties from each line; else use single uploader state
      if (this.isBundleOpen()) {
        const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
        if (openItem) {
          const uploaders = openItem.querySelectorAll('.bundle-uploader-line .kid-info-section');
          uploaders.forEach((uploader, idx) => {
            const nameInput = uploader.querySelector('#kid-name-input');
            const nameVal = (nameInput?.value || '').trim();
            if (nameVal) {
              const kid = document.createElement('input');
              kid.type = 'hidden';
              kid.name = `properties[Kid Name ${idx + 1}]`;
              kid.value = nameVal;
              this.formEl.appendChild(kid);
            }
            const ageInput = uploader.querySelector('.kid-age');
            const ageVal = (ageInput?.value || '').trim();
            if (ageVal) {
              const age = document.createElement('input');
              age.type = 'hidden';
              age.name = `properties[Kid Age ${idx + 1}]`;
              age.value = ageVal;
              this.formEl.appendChild(age);
            }
            const photos = uploader.querySelectorAll('.multi-photo-grid .photo-preview.multi-item');
            photos.forEach((p, pi) => {
              const url = p.getAttribute('data-image-url') || '';
              if (url) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = `properties[Photo ${idx + 1}-${pi + 1}]`;
                input.value = url;
                this.formEl.appendChild(input);
              }
            });
          });
        }
      } else {
        // Single flow – use this.processed and single kid name
        this.processed.forEach((item, i) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[Photo ${i + 1}]`;
          input.value = item.azureUrl || item.previewUrl;
          this.formEl.appendChild(input);
        });
      }
    } catch (err) {
      console.error('❌ Failed syncing hidden inputs:', err);
    }
  }

  syncHiddenInputsIntoForm(targetForm) {
    try {
      if (!targetForm) return;
      // Clean previous
      Array.from(targetForm.querySelectorAll('input[name^="properties[Photo "]')).forEach(el => el.remove());

      let kid = targetForm.querySelector('input[name="properties[Kid Name]"]');
      const kidValue = (this.kidNameInput?.value || '').trim();
      if (!kid) {
        kid = document.createElement('input');
        kid.type = 'hidden';
        kid.name = 'properties[Kid Name]';
        targetForm.appendChild(kid);
      }
      kid.value = kidValue;

      let age = targetForm.querySelector('input[name="properties[Kid Age]"]');
      const ageValue = (this.kidAgeInput?.value || '').trim();
      if (!age) {
        age = document.createElement('input');
        age.type = 'hidden';
        age.name = 'properties[Kid Age]';
        targetForm.appendChild(age);
      }
      age.value = ageValue;

      if (this.isBundleOpen()) {
        // Remove any Kid Name X and Kid Age X
        Array.from(targetForm.querySelectorAll('input[name^="properties[Kid Name "]')).forEach(el => el.remove());
        Array.from(targetForm.querySelectorAll('input[name^="properties[Kid Age "]')).forEach(el => el.remove());
        const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
        if (openItem) {
          const uploaders = openItem.querySelectorAll('.bundle-uploader-line .kid-info-section');
          uploaders.forEach((uploader, idx) => {
            const nameInput = uploader.querySelector('#kid-name-input');
            const nameVal = (nameInput?.value || '').trim();
            if (nameVal) {
              const kidx = document.createElement('input');
              kidx.type = 'hidden';
              kidx.name = `properties[Kid Name ${idx + 1}]`;
              kidx.value = nameVal;
              targetForm.appendChild(kidx);
            }
            const ageInput = uploader.querySelector('.kid-age');
            const ageVal = (ageInput?.value || '').trim();
            if (ageVal) {
              const agex = document.createElement('input');
              agex.type = 'hidden';
              agex.name = `properties[Kid Age ${idx + 1}]`;
              agex.value = ageVal;
              targetForm.appendChild(agex);
            }
            const photos = uploader.querySelectorAll('.multi-photo-grid .photo-preview.multi-item');
            photos.forEach((p, pi) => {
              const url = p.getAttribute('data-image-url') || '';
              if (url) {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = `properties[Photo ${idx + 1}-${pi + 1}]`;
                input.value = url;
                targetForm.appendChild(input);
              }
            });
          });
        }
      } else {
        this.processed.forEach((item, i) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[Photo ${i + 1}]`;
          input.value = item.azureUrl || item.previewUrl;
          targetForm.appendChild(input);
        });
      }
    } catch (e) {
      console.error('❌ syncHiddenInputsIntoForm failed', e);
    }
  }

  setupLanguageValidation() {
    try {
      if (!this.kidNameInput) return;
      const isBundleContext = !!this.container?.closest('.bundle-uploader');
      const storyLanguageRaw = (this.container?.dataset?.storyLanguage || '').toString().trim().toLowerCase();
      const isArabic = storyLanguageRaw === 'arabic' || storyLanguageRaw === 'ar';

      // Placeholder
      const placeholder = isArabic ? 'أدخل اسم الطفل' : "Enter child's name";
      this.kidNameInput.setAttribute('placeholder', placeholder);
      this.kidNameInput.setAttribute('maxlength', '15');

      // In bundle context, accept any letters and no language hints
      if (isBundleContext) {
        const onInput = (e) => {
          let v = e.target.value || '';
          // Allow Arabic + Latin letters and spaces only
          const allowed = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]/g;
          v = (v.match(allowed) || []).join('');
          // Remove Arabic-Indic and Eastern Arabic-Indic digits (and ASCII digits just in case)
          v = v.replace(/[0-9\u0660-\u0669\u06F0-\u06F9]/g, '');
          e.target.value = v.slice(0, 15);
          this.syncHiddenInputs();
          this.updateButtonsState();
        };
        this.kidNameInput.addEventListener('input', onInput);
        return;
      }

      // Validation handlers
      const onInput = (e) => {
        let v = e.target.value || '';
        // Accept Arabic and Latin letters for all languages
        const allowed = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]/g;
        v = (v.match(allowed) || []).join('');
        // Strip digits (ASCII + Arabic-Indic + Eastern Arabic-Indic)
        v = v.replace(/[0-9\u0660-\u0669\u06F0-\u06F9]/g, '');
        e.target.value = v.slice(0, 15);
        this.syncHiddenInputs();
        this.checkNameValidation();
        this.updateButtonsState();
      };
      this.kidNameInput.addEventListener('input', onInput);
      this.kidNameInput.addEventListener('blur', () => this.checkNameValidation());
      this.kidNameInput.addEventListener('focus', () => this.checkNameValidation());
    } catch (e) {
      console.error('❌ setupLanguageValidation failed', e);
    }
  }

  checkNameValidation() {
    try {
      if (!this.kidNameInput) return;
      const v = this.kidNameInput.value || '';

      // Detect language from inputs/metafields
      const storyLanguage = this.getStoryLanguage();
      const isArabic = storyLanguage === 'Arabic';

      // Patterns to detect character scripts
      const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
      const latinPattern = /[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF]/;

      let valid = true;
      if (v && v.trim()) {
        const hasArabic = arabicPattern.test(v);
        const hasLatin = latinPattern.test(v);

        if (isArabic) {
          // For Arabic stories, invalid if has Latin characters WITHOUT any Arabic
          // (Or strictly enforcing Arabic characters only, depending on preference.
          // The previous logic was: invalid if has Latin && !hasArabic)
          valid = !(hasLatin && !hasArabic);
        } else {
          // For non-Arabic stories, invalid if has Arabic characters WITHOUT any Latin
          valid = !(hasArabic && !hasLatin);
        }
      }

      if (!valid) {
        // Optional: visual feedback handled here if needed
      } else {
        this.hideValidationHint();
      }
    } catch (e) {
      console.error('❌ checkNameValidation failed', e);
    }
  }

  showValidationHint(message) {
    try {
      this.hideValidationHint();
      const input = this.kidNameInput;
      if (!input) return;
      const hint = document.createElement('div');
      hint.className = 'english-validation-hint';
      hint.innerHTML = `
        <div class="hint-content">
          <i class="fas fa-info-circle"></i>
          <span>${message}</span>
        </div>
      `;
      const rect = input.getBoundingClientRect();
      hint.style.position = 'fixed';
      hint.style.top = `${Math.max(10, rect.top - 80)}px`;
      hint.style.left = `${rect.left}px`;
      hint.style.width = `${rect.width}px`;
      hint.style.zIndex = '1000';
      document.body.appendChild(hint);
      // basic styles if not present
      if (!document.querySelector('#english-hint-styles')) {
        const styles = document.createElement('style');
        styles.id = 'english-hint-styles';
        styles.textContent = `
          .english-validation-hint { background:#fef3c7; border:1px solid #f59e0b; border-radius:6px; padding:8px 12px; font-size:13px; color:#92400e; box-shadow:0 2px 8px rgba(0,0,0,.1); }
          .english-validation-hint .hint-content { display:flex; align-items:center; gap:8px; }
          .english-validation-hint i { color:#f59e0b; font-size:14px; }
        `;
        document.head.appendChild(styles);
      }
    } catch (e) {
      console.error('❌ showValidationHint failed', e);
    }
  }

  hideValidationHint() {
    const hint = document.querySelector('.english-validation-hint');
    if (hint) hint.remove();
  }

  isArabicLanguage() {
    const storyLanguageRaw = (this.container?.dataset?.storyLanguage || '').toString().trim().toLowerCase();
    return storyLanguageRaw === 'arabic' || storyLanguageRaw === 'ar';
  }

  isValidName() {
    const v = (this.kidNameInput?.value || '').trim();
    if (v.length < 2) return false;
    // Reject any digits: ASCII, Arabic-Indic, Eastern Arabic-Indic
    if (/[0-9\u0660-\u0669\u06F0-\u06F9]/.test(v)) return false;
    // Accept Arabic or Latin letters universally
    const bothRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFFa-zA-ZÀ-ÿ\u00C0-\u017F\u0180-\u024F\u1E00-\u1EFF\u2C60-\u2C7F\uA720-\uA7FF\s]+$/;
    return bothRegex.test(v);
  }

  isFormValid() {
    const hasImages = this.processed.length > 0;
    const nameValid = this.isValidName();
    return hasImages && nameValid;
  }

  updateButtonsState() {
    try {
      const addBtn = this.formEl?.querySelector('button[name="add"], .product-form__submit[name="add"]');
      const paymentBtn = this.formEl?.querySelector('.shopify-payment-button__button, .shopify-payment-button__button--unbranded');
      const paymentContainer = this.formEl?.querySelector('.shopify-payment-button');
      const pfContainer = this.getProductFormContainer();

      // Use the EXACT same validation logic as global guards: this.isBundleOpen() ? this.isOpenBundleValid() : this.isFormValid()
      const valid = this.isBundleOpen() ? this.isOpenBundleValid() : this.isFormValid();

      // Clean up any prior overlays or styles if they existed
      const cleanupBtn = (btn) => {
        if (!btn) return;
        btn.removeAttribute('data-guard-disabled');
        btn.removeAttribute('aria-disabled');
        btn.disabled = false;
        btn.style.opacity = '';
        btn.style.cursor = '';
        this.removeOverlay(btn);
      };
      cleanupBtn(addBtn);
      cleanupBtn(paymentBtn);

      // HARD BLOCK: Disable dynamic checkout when invalid
      if (paymentContainer) {
        // Hide dynamic checkout entirely when bundle UI exists (bundle page)
        const isBundlePage = !!document.querySelector('.bundle-accordion');
        if (isBundlePage) {
          paymentContainer.style.display = 'none';
        } else {
          paymentContainer.style.display = '';
        }
        const ensureHardBlockHandler = () => {
          if (paymentContainer._hardBlockHandler) return;
          paymentContainer._hardBlockHandler = (e) => {
            const stillInvalid = !(this.isBundleOpen() ? this.isOpenBundleValid() : this.isFormValid());
            if (!stillInvalid) {
              // Inputs are now valid; remove blocker and allow checkout
              try { paymentContainer.removeEventListener('click', paymentContainer._hardBlockHandler, true); } catch { }
              delete paymentContainer._hardBlockHandler;
              delete paymentContainer.dataset.hardBlockAttached;
              return; // allow event to proceed
            }
            e.preventDefault();
            e.stopPropagation();
            if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
            this.showToaster('Please upload kid images and write kid name before placing Order', 'error');
            this.scrollToKidNameField();
            return false;
          };
          paymentContainer.addEventListener('click', paymentContainer._hardBlockHandler, true);
          paymentContainer.dataset.hardBlockAttached = 'true';
        };

        if (!valid) {
          paymentContainer.style.pointerEvents = 'none';
          paymentContainer.style.opacity = '0.5';
          paymentContainer.style.cursor = 'not-allowed';
          paymentContainer.setAttribute('aria-disabled', 'true');
          ensureHardBlockHandler();
        } else {
          paymentContainer.style.pointerEvents = '';
          paymentContainer.style.opacity = '';
          paymentContainer.style.cursor = '';
          paymentContainer.removeAttribute('aria-disabled');
          if (paymentContainer._hardBlockHandler) {
            try { paymentContainer.removeEventListener('click', paymentContainer._hardBlockHandler, true); } catch { }
            delete paymentContainer._hardBlockHandler;
            delete paymentContainer.dataset.hardBlockAttached;
          }
        }
      }

      if (pfContainer) {
        pfContainer.removeAttribute('data-guard-invalid');
        const ov = pfContainer.querySelector(':scope > .guard-overlay-form');
        if (ov) ov.remove();
      }

      // Reflect correct total amount on Dynamic Checkout when bundle is open
      try {
        if (paymentContainer && paymentContainer.style.display !== 'none') {
          this.updatePaymentButtonAmount(paymentContainer);
        }
      } catch { }
    } catch (e) {
      // non-fatal
    }
  }

  attachFormGuards() {
    try {
      if (!this.formEl) return;

      const guard = (e) => {
        // Always resync hidden inputs before submit
        this.setBundleVariantOnForm();
        this.syncHiddenInputs();

        // Validate kid name language
        const languageValidation = this.validateKidNameLanguage();
        if (!languageValidation.valid) {
          e.preventDefault();
          this.showToaster(languageValidation.message, 'error');
          this.scrollToKidNameField();
          return false;
        }

        // Extra guard: if bundle exists but no variant selected anywhere, treat as invalid
        const bundleRoot = document.querySelector('.bundle-accordion');
        const noBundleSelection = !!bundleRoot && !bundleRoot.querySelector('.bundle-line .bundle-variant option:checked[value]:not([value=""])');
        const valid = (!noBundleSelection) && this.isOverallValid();
        if (!valid) {
          e.preventDefault();
          this.showToaster('Please upload kid images and write kid name before placing Order', 'error');
          this.scrollToKidNameField();
          return false;
        }
        return true;
      };

      this.formEl.addEventListener('submit', guard, true);

      const addBtn = this.formEl.querySelector('button[name="add"]');
      if (addBtn) {
        if (!addBtn.dataset.guardAttached) {
          addBtn.addEventListener('click', (e) => {
            if (addBtn.getAttribute('data-guard-disabled') === 'true' || !guard(e)) return;
          }, true);
          addBtn.dataset.guardAttached = 'true';
        }
      }
      const paymentBtn = this.formEl.querySelector('.shopify-payment-button__button, .shopify-payment-button__button--unbranded');
      if (paymentBtn) {
        if (!paymentBtn.dataset.guardAttached) {
          paymentBtn.addEventListener('click', (e) => {
            if (paymentBtn.getAttribute('data-guard-disabled') === 'true' || !guard(e)) return;
          }, true);
          paymentBtn.dataset.guardAttached = 'true';
        }
      }
      // Attach guard on the payment host element to catch shadow DOM interactions
      const paymentHost = this.formEl.querySelector('shopify-payment-button');
      if (paymentHost && !paymentHost.dataset.guardAttached) {
        const intercept = (e) => {
          if (!guard(e)) return;
        };
        paymentHost.addEventListener('click', intercept, true);
        paymentHost.addEventListener('pointerdown', intercept, true);
        paymentHost.addEventListener('mousedown', intercept, true);
        paymentHost.dataset.guardAttached = 'true';
      }
    } catch (e) {
      console.error('❌ attachFormGuards failed', e);
    }
  }

  ensureOverlay(btn) {
    try {
      const parent = btn?.parentElement;
      if (!parent) return;
      if (!parent.style.position || parent.style.position === 'static') {
        parent.style.position = 'relative';
      }
      let overlay = parent.querySelector('.guard-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'guard-overlay';
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.zIndex = '5';
        overlay.style.background = 'transparent';
        overlay.setAttribute('role', 'button');
        overlay.setAttribute('tabindex', '0');
        const showMsg = () => {
          this.showToaster('Please upload kid images and write kid name before placing Order', 'error');
          this.scrollToKidNameField();
        };
        overlay.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          showMsg();
        }, true);
        overlay.addEventListener('keydown', (e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            showMsg();
          }
        }, true);
        parent.appendChild(overlay);
      }
    } catch (e) { /* ignore */ }
  }

  removeOverlay(btn) {
    try {
      const parent = btn?.parentElement;
      if (!parent) return;
      const overlay = parent.querySelector('.guard-overlay');
      if (overlay) overlay.remove();
    } catch (e) { /* ignore */ }
  }

  observeFormMutations() {
    try {
      if (!this.formEl) return;
      const observer = new MutationObserver((mutations) => {
        try {
          // Prevent infinite loops by ignoring mutations we caused
          // Look for our overlays or hidden inputs and ignore when only those changed
          const onlyGuard = mutations.every(m =>
            Array.from(m.addedNodes).every(n => n.nodeType !== 1 || (n.classList?.contains('guard-overlay') || n.classList?.contains('guard-overlay-form'))) &&
            Array.from(m.removedNodes).every(n => n.nodeType !== 1 || (n.classList?.contains('guard-overlay') || n.classList?.contains('guard-overlay-form')))
          );
          if (!onlyGuard) {
            this.attachFormGuards();
            this.updateButtonsState();
            this.installPaymentShadowGuards();
          }
        } catch {
          this.attachFormGuards();
          this.updateButtonsState();
          this.installPaymentShadowGuards();
        }
      });
      observer.observe(this.formEl, { childList: true, subtree: true });
      this._formObserver = observer;
    } catch (e) {
      // ignore
    }
  }

  installPaymentShadowGuards() {
    try {
      // Use the EXACT same validation logic as the working global guards
      const validateCheckout = () => {
        const globalUploader = window.productMultiUploader;
        if (!globalUploader) {
          console.warn('🔍 No global uploader found, allowing checkout');
          return true;
        }

        // Use the EXACT same logic as global guards: this.isBundleOpen() ? this.isOpenBundleValid() : this.isFormValid()
        const valid = globalUploader.isBundleOpen() ? globalUploader.isOpenBundleValid() : globalUploader.isFormValid();

        console.log('🔍 Dynamic checkout validation (mobile-friendly):', {
          valid,
          isBundleOpen: globalUploader.isBundleOpen(),
          globalUploaderExists: !!globalUploader,
          userAgent: navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop'
        });

        return valid;
      };

      // Install comprehensive event blocking for all dynamic checkout elements
      const installBlockingForHost = (host) => {
        if (host.dataset.mobileGuardAttached) return;

        const blockInvalidCheckout = (e) => {
          const globalUploader = window.productMultiUploader;
          const isValid = validateCheckout();
          if (!isValid) {
            e.preventDefault();
            e.stopPropagation();
            if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
            if (globalUploader) {
              globalUploader.showToaster('Please upload kid images and write kid name before placing Order', 'error');
              globalUploader.scrollToKidNameField();
            }
            return false;
          }

          // Override Apple Pay / Dynamic Checkout even when valid to ensure correct total and properties, especially for bundles
          try {
            if (!globalUploader) return true;
            // If bundle page: dynamic checkout is hidden; block Apple Pay entirely
            const isBundlePage = !!document.querySelector('.bundle-accordion');
            if (isBundlePage) {
              e.preventDefault();
              e.stopPropagation();
              if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
              return false;
            }
            // Avoid double-processing
            if (globalUploader.isAdding) {
              e.preventDefault();
              e.stopPropagation();
              if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
              return false;
            }

            // If bundle open, add multiple lines and go to checkout; else single add then checkout
            if (globalUploader.isBundleOpen()) {
              const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
              const lines = globalUploader.collectBundleLines(openItem);
              if (Array.isArray(lines) && lines.length >= 1) {
                e.preventDefault();
                e.stopPropagation();
                if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
                (async () => {
                  try {
                    globalUploader.isAdding = true;
                    globalUploader.updateButtonsState();
                    const items = globalUploader.buildBundleItems(lines);
                    const ok = await globalUploader.addMultipleToCartViaAjax(items);
                    if (!ok) {
                      globalUploader.showToaster('Failed to add items. Please try again.', 'error');
                    }
                  } catch (err) {
                    console.error('❌ ApplePay override (bundle) failed', err);
                    globalUploader.showToaster('Something went wrong. Please try again.', 'error');
                  } finally {
                    globalUploader.isAdding = false;
                    globalUploader.updateButtonsState();
                  }
                })();
                return false;
              }
            } else {
              // Single flow override: ensure properties captured; do not redirect
              e.preventDefault();
              e.stopPropagation();
              if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
              (async () => {
                try {
                  globalUploader.isAdding = true;
                  globalUploader.updateButtonsState();
                  const ok = await globalUploader.addToCartViaAjax();
                  if (!ok) {
                    globalUploader.showToaster('Failed to start checkout. Please try again.', 'error');
                  }
                } finally {
                  globalUploader.isAdding = false;
                  globalUploader.updateButtonsState();
                }
              })();
              return false;
            }
          } catch { }

          return true;
        };

        // Attach to multiple event types for better mobile compatibility
        const events = ['click', 'touchstart', 'pointerdown', 'mousedown'];
        events.forEach(eventType => {
          host.addEventListener(eventType, blockInvalidCheckout, true);
        });

        // Also try to block shadow DOM events
        const wireShadow = () => {
          try {
            const root = host.shadowRoot;
            if (root) {
              const buttons = root.querySelectorAll('button, [role="button"], .shopify-payment-button__button');
              buttons.forEach(btn => {
                if (!btn.dataset.mobileGuardAttached) {
                  events.forEach(eventType => {
                    btn.addEventListener(eventType, blockInvalidCheckout, true);
                  });
                  btn.dataset.mobileGuardAttached = 'true';
                }
              });
            }
          } catch { }
        };

        // Wire immediately and observe for changes
        wireShadow();
        if (!host._mobileObserver) {
          try {
            const observer = new MutationObserver(wireShadow);
            observer.observe(host, { childList: true, subtree: true });
            host._mobileObserver = observer;
          } catch { }
        }

        host.dataset.mobileGuardAttached = 'true';
      };

      // Install on all existing payment buttons
      const hosts = document.querySelectorAll('shopify-payment-button');
      console.log('🔍 Installing mobile-friendly guards on hosts:', hosts.length);
      hosts.forEach((host) => {
        installBlockingForHost(host);
        try { this.updatePaymentButtonAmount(host.parentElement || host); } catch { }
      });

      // Watch for new payment buttons being added
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              if (node.matches && node.matches('shopify-payment-button')) {
                installBlockingForHost(node);
              }
              const newHosts = node.querySelectorAll && node.querySelectorAll('shopify-payment-button');
              if (newHosts) {
                newHosts.forEach(installBlockingForHost);
              }
            }
          });
        });
      });

      observer.observe(document.body, { childList: true, subtree: true });

    } catch (e) {
      console.error('❌ installPaymentShadowGuards failed', e);
    }
  }

  // Compute bundle total and reflect it on the payment button label/adjacent UI
  updatePaymentButtonAmount(containerEl) {
    try {
      // Only for bundle context; otherwise, let Shopify render default amount
      if (!this.isBundleOpen()) {
        this.resetPaymentButtonAmount(containerEl);
        return;
      }

      const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
      if (!openItem) {
        this.resetPaymentButtonAmount(containerEl);
        return;
      }
      const size = parseInt(openItem.getAttribute('data-size'), 10) || 1;
      if (size <= 1) {
        this.resetPaymentButtonAmount(containerEl);
        return;
      }

      // Extract the per-unit bundle price text (e.g., "SAR 199.00/each")
      const unitEl = openItem.querySelector('.bundle-summary-right .bundle-price');
      const unitText = (unitEl?.textContent || '').trim();
      if (!unitText) {
        this.resetPaymentButtonAmount(containerEl);
        return;
      }

      // Parse currency prefix and numeric portion
      // Examples we support: "$199.00/each", "SAR 199.00/each", "€199,00/each"
      const cleaned = unitText.split('/')[0].trim();
      // Capture leading currency chunk (non-digit including spaces) and number with decimal/comma
      const match = cleaned.match(/^([^0-9\-]*?)\s*([0-9.,\-]+)/);
      if (!match) {
        this.resetPaymentButtonAmount(containerEl);
        return;
      }
      const currencyPrefix = (match[1] || '').trim();
      const numberPart = (match[2] || '').trim();

      // Normalize to dot decimals: if comma present and dot not present, treat comma as decimal
      let normalized = numberPart;
      if (numberPart.indexOf(',') >= 0 && numberPart.indexOf('.') < 0) {
        normalized = numberPart.replace(/\./g, '').replace(/,/g, '.');
      } else {
        normalized = numberPart.replace(/,/g, '');
      }
      const unit = parseFloat(normalized);
      if (!isFinite(unit) || unit <= 0) {
        this.resetPaymentButtonAmount(containerEl);
        return;
      }
      const total = unit * size;

      const formatted = this.formatMoneyFromPrefix(total, currencyPrefix || '');

      // Try to update unbranded button label inline
      const btn = (containerEl.querySelector && containerEl.querySelector('.shopify-payment-button__button--unbranded')) || null;
      if (btn) {
        if (!btn.dataset.originalLabel) {
          btn.dataset.originalLabel = btn.textContent || '';
        }
        const base = btn.dataset.originalLabel || 'Buy it now';
        btn.textContent = `${base} • ${formatted}`;
      }

      // Add/update adjacent total label for branded buttons
      const holder = (containerEl.closest && containerEl.closest('.shopify-payment-button')) || containerEl;
      if (holder) {
        let badge = holder.querySelector('.bundle-dco-amount');
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'bundle-dco-amount';
          badge.style.fontSize = '12px';
          badge.style.fontWeight = '700';
          badge.style.marginTop = '6px';
          badge.style.textAlign = 'center';
          badge.style.color = '#111827';
          holder.appendChild(badge);
        }
        badge.textContent = `Total: ${formatted}`;
      }
    } catch (e) {
      // Do not block UX on formatting issues
    }
  }

  resetPaymentButtonAmount(containerEl) {
    try {
      const btn = containerEl?.querySelector?.('.shopify-payment-button__button--unbranded');
      if (btn && btn.dataset && btn.dataset.originalLabel) {
        btn.textContent = btn.dataset.originalLabel;
      }
      const holder = (containerEl?.closest && containerEl.closest('.shopify-payment-button')) || containerEl;
      if (holder) {
        const badge = holder.querySelector?.('.bundle-dco-amount');
        if (badge) badge.remove();
      }
    } catch { }
  }

  formatMoneyFromPrefix(amount, currencyPrefix) {
    try {
      // Keep two decimals; prefix is whatever Shopify rendered for unit price
      const rounded = Math.round(amount * 100) / 100;
      const parts = rounded.toFixed(2);
      if (!currencyPrefix) return parts;
      return `${currencyPrefix} ${parts}`.trim();
    } catch {
      return String(amount);
    }
  }

  installGlobalGuards() {
    try {
      // Capture clicks on add-to-cart and payment buttons anywhere
      document.addEventListener('click', async (e) => {
        const target = e.target;
        if (!target) return;
        const isAdd = !!target.closest('button.product-form__submit[name="add"], .product-form__submit[name="add"], button[name="add"][type="submit"]');
        const isPayment = !!(target.closest('.shopify-payment-button__button, .shopify-payment-button__button--unbranded') || target.closest('shopify-payment-button'));
        if (!isAdd && !isPayment) return;
        // Prevent re-entrancy if already processing
        if (this._handlingClick) return;
        this._handlingClick = true;
        const valid = this.isBundleOpen() ? this.isOpenBundleValid() : this.isFormValid();
        console.log('🔍 Global guard validation:', {
          valid,
          isBundleOpen: this.isBundleOpen(),
          isAdd,
          isPayment,
          eventType: e.type,
          targetTag: target?.tagName || 'unknown'
        });

        if (!valid) {
          e.preventDefault();
          if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
          e.stopPropagation();
          this.updateButtonsState();
          this.showToaster('Please upload kid images and write kid name before placing Order', 'error');
          this.scrollToKidNameField();
          this._handlingClick = false;
          return;
        } else if (isAdd || isPayment) {
          // Bundle: if an accordion is open and has 2+ stories, add each as its own line
          if (this.isBundleOpen()) {
            const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
            const lines = this.collectBundleLines(openItem);
            if (Array.isArray(lines) && lines.length >= 1) {
              e.preventDefault();
              e.stopPropagation();
              if (this.isAdding) return;
              try {
                this.isAdding = true;
                this.updateButtonsState();
                const items = this.buildBundleItems(lines);
                const ok = await this.addMultipleToCartViaAjax(items);
                if (ok) {
                  // For bundle, proceed directly to checkout on success for both add and payment
                  this.navigateToCheckout();
                } else {
                  this.showToaster('Failed to add items. Please try again.', 'error');
                }
              } catch (err) {
                console.error('❌ Bundle add flow failed', err);
                this.showToaster('Something went wrong. Please try again.', 'error');
              } finally {
                this.isAdding = false;
                this.updateButtonsState();
              }
              return;
            }
          }

          // Single flow: Ensure properties synced, then allow native submission or override payment
          try {
            this.setBundleVariantOnForm();
            await this.syncHiddenInputs();
          } catch { }
          if (isPayment) {
            // On non-bundle pages we may still override dynamic checkout for properties,
            // but do NOT redirect automatically; let Shopify handle next step if needed.
            e.preventDefault();
            e.stopPropagation();
            if (this.isAdding) return;
            try {
              this.isAdding = true;
              this.updateButtonsState();
              const ok = await this.addToCartViaAjax();
              if (!ok) {
                this.showToaster('Failed to start checkout. Please try again.', 'error');
              }
            } finally {
              this.isAdding = false;
              this.updateButtonsState();
            }
          }
        }
        this._handlingClick = false;
      }, true);

      // Capture submit on any product form
      document.addEventListener('submit', (e) => {
        const form = e.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (form.getAttribute('data-type') !== 'add-to-cart-form') return;
        // Always ensure latest properties are present synchronously before submit
        if (form === this.formEl) {
          this.setBundleVariantOnForm();
          this.syncHiddenInputsSync();
        }
        const valid = this.isBundleOpen() ? this.isOpenBundleValid() : this.isFormValid();
        if (!valid) {
          e.preventDefault();
          e.stopPropagation();
          this.updateButtonsState();
          // Silent guard: do not show toaster
          this.scrollToKidNameField();
        }
      }, true);
    } catch (e) {
      // ignore
    }
  }

  navigateToCheckout() {
    try {
      // Use a small async defer to ensure UI unblocks before navigation
      setTimeout(() => { window.location.assign('/checkout'); }, 50);
    } catch {
      window.location.href = '/checkout';
    }
  }

  installActionTracking() {
    try {
      // Future: track last action if needed
    } catch { }
  }

  // ---------- Upsell after add (single product only) ----------
  installAddSuccessListener() {
    try {
      // Skip on bundle pages
      if (document.querySelector('.bundle-accordion')) return;
      if (window.__upsellListenerInstalled) return;
      window.__upsellListenerInstalled = true;

      // Patch fetch
      try {
        const origFetch = window.fetch;
        if (origFetch && !origFetch.__upsellWrapped) {
          const self = this;
          window.fetch = async function (input, init) {
            const resp = await origFetch.apply(this, arguments);
            try {
              const url = (typeof input === 'string') ? input : (input?.url || '');
              const method = (init?.method || (input?.method) || 'GET').toUpperCase();
              if (method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url) && resp && resp.ok) {
                setTimeout(() => self.handleAddSuccess(), 10);
              }
            } catch { }
            return resp;
          };
          window.fetch.__upsellWrapped = true;
        }
      } catch { }

      // Patch XHR
      try {
        const OrigXHR = window.XMLHttpRequest;
        if (OrigXHR && !OrigXHR.prototype.__upsellWrapped) {
          const self = this;
          const open = OrigXHR.prototype.open;
          const send = OrigXHR.prototype.send;
          OrigXHR.prototype.open = function (method, url) {
            this.__upsellMethod = (method || 'GET').toString().toUpperCase();
            this.__upsellUrl = (url || '').toString();
            return open.apply(this, arguments);
          };
          OrigXHR.prototype.send = function () {
            this.addEventListener('load', function () {
              try {
                if (this.__upsellMethod === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(this.__upsellUrl) && this.status >= 200 && this.status < 300) {
                  setTimeout(() => self.handleAddSuccess(), 10);
                }
              } catch { }
            });
            return send.apply(this, arguments);
          };
          OrigXHR.prototype.__upsellWrapped = true;
        }
      } catch { }
    } catch { }
  }

  async handleAddSuccess() {
    try {
      // Check if upsell popup is enabled in theme settings
      if (!this.isUpsellPopupEnabled()) return;

      // Single product only
      if (document.querySelector('.bundle-accordion')) return;
      if (this._upsellShown) return;
      this._upsellShown = true;

      const kidName = (this.kidNameInput?.value || '').trim();
      const namePart = kidName ? kidName : 'your child';
      const maxProducts = this.getUpsellMaxProducts();
      const products = await this.fetchRecommendations(maxProducts);

      // Apply delay if configured
      const delay = this.getUpsellPopupDelay();
      if (delay > 0) {
        setTimeout(() => {
          this.showUpsellPopup(namePart, products);
        }, delay);
      } else {
        this.showUpsellPopup(namePart, products);
      }
    } catch { }
  }

  // Helper methods to read theme settings
  isUpsellPopupEnabled() {
    try {
      return window.Shopify?.theme?.settings?.enable_upsell_popup !== false;
    } catch {
      return true; // Default to enabled if settings not available
    }
  }

  getUpsellPopupDelay() {
    try {
      return parseInt(window.Shopify?.theme?.settings?.upsell_popup_delay) || 0;
    } catch {
      return 0;
    }
  }

  getUpsellMaxProducts() {
    try {
      return parseInt(window.Shopify?.theme?.settings?.upsell_popup_max_products) || 8;
    } catch {
      return 8;
    }
  }

  getUpsellPopupTitle() {
    try {
      return window.Shopify?.theme?.settings?.upsell_popup_title || 'Does {child_name} have a brother or sister?';
    } catch {
      return 'Does {child_name} have a brother or sister?';
    }
  }

  getUpsellPopupOfferText() {
    try {
      return window.Shopify?.theme?.settings?.upsell_popup_offer_text || 'Add another Story and get 100 EGP OFF';
    } catch {
      return 'Add another Story and get 100 EGP OFF';
    }
  }

  async fetchRecommendations(limit = 8) {
    try {
      // Try ShopifyAnalytics product id first
      let productId = null;
      try { productId = window?.ShopifyAnalytics?.meta?.product?.id; } catch { }
      if (!productId) {
        try {
          const pr = document.querySelector('product-recommendations[data-product-id]');
          if (pr) productId = pr.getAttribute('data-product-id');
        } catch { }
      }
      if (!productId) return [];
      const url = `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}`;
      const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return [];
      const data = await res.json();
      return Array.isArray(data?.products) ? data.products : [];
    } catch { return []; }
  }

  showUpsellPopup(namePart, products) {
    try {
      // Build or reuse popup container
      let overlay = document.querySelector('#upsell-related-overlay');
      if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'upsell-related-overlay';
        overlay.style.position = 'fixed';
        overlay.style.inset = '0';
        overlay.style.background = 'rgba(0,0,0,0.5)';
        overlay.style.zIndex = '10000';
        overlay.style.display = 'flex';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        document.body.appendChild(overlay);
      }

      let modal = document.querySelector('#upsell-related-modal');
      if (!modal) {
        modal = document.createElement('div');
        modal.id = 'upsell-related-modal';
        modal.style.background = '#fff';
        modal.style.borderRadius = '14px';
        modal.style.width = 'min(960px, 92vw)';
        modal.style.maxHeight = '86vh';
        modal.style.overflow = 'hidden';
        modal.style.boxShadow = '0 20px 50px rgba(0,0,0,.25)';
        overlay.appendChild(modal);
      }

      // Get customizable text from theme settings
      const titleTemplate = this.getUpsellPopupTitle();
      const title = titleTemplate.replace('{child_name}', `<span style="text-decoration:underline;">${this.escapeHtml(namePart)}</span>`);
      const offerText = this.getUpsellPopupOfferText();

      const headerHtml = `
        <div style="padding:16px 18px; background:#856DEC; color:#fff; display:flex; align-items:center; justify-content:space-between; gap:10px;">
          <div style="font-weight:800; font-size:16px;">
            ${title}
          </div>
          <button id="upsell-related-close" aria-label="Close" style="background:transparent;border:none;color:#fff;font-size:20px;cursor:pointer;">×</button>
        </div>`;
      const offerHtml = `
        <div style="padding:10px 18px; background:#fef3c7; color:#92400e; font-weight:700; border-bottom:1px solid #fde68a;">
          ${this.escapeHtml(offerText)}
        </div>`;
      const listHtml = this.renderRecommendationsCarousel(products);
      modal.innerHTML = headerHtml + offerHtml + listHtml;

      const close = () => { try { overlay.remove(); } catch { } };
      overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });
      const closeBtn = modal.querySelector('#upsell-related-close');
      if (closeBtn) closeBtn.addEventListener('click', close);
    } catch { }
  }

  renderRecommendationsCarousel(products) {
    try {
      const items = Array.isArray(products) ? products : [];
      if (!items.length) {
        return `<div style="padding:18px;">No suggestions right now.</div>`;
      }
      const cards = items.map((p) => {
        const img = (p?.featured_image?.url || p?.image || p?.images?.[0] || '').toString();
        const url = (p?.url || `/products/${p?.handle || ''}`).toString();
        const title = this.escapeHtml(p?.title || '');
        return `
          <div class="upsell-card" style="flex:0 0 210px; display:flex; flex-direction:column; justify-content:space-between; color:inherit; text-decoration:none; background:#fff; height:auto;">
            <div style="border:1px solid #e5e7eb; border-radius:10px; overflow:hidden; background:#fff;">
              <a href="${this.escapeAttr(url)}" style="display:block; color:inherit; text-decoration:none;">
                <div style="width:100%;height:180px;background:#f3f4f6;display:flex;align-items:center;justify-content:center;">
                  ${img ? `<img loading="lazy" src="${this.escapeAttr(img)}" alt="" style="max-width:100%;max-height:100%;object-fit:cover;">` : ''}
                </div>
                <div style="padding:10px 12px; font-weight:700; font-size:14px;">${title}</div>
              </a>
            </div>
            <div style="padding:10px 0 0 0; display:flex; margin-top:auto;">
              <a href="${this.escapeAttr(url)}" class="card__personalize-button" style="background:#856DEC; box-shadow: 0 2px 8px rgba(133, 109, 236, 0.10);">Personalize</a>
            </div>
          </div>`;
      }).join('');

      const html = `
        <div style="position:relative; padding:14px 42px; background:#fff;">
          <button class="upsell-nav prev" aria-label="Prev" style="position:absolute;left:6px;top:50%;transform:translateY(-50%);border:none;background:#111827;color:#fff;width:32px;height:32px;border-radius:999px;cursor:pointer;">‹</button>
          <div class="upsell-track" style="display:flex; gap:12px; overflow:auto; scroll-behavior:smooth; padding:2px;">
            ${cards}
          </div>
          <button class="upsell-nav next" aria-label="Next" style="position:absolute;right:6px;top:50%;transform:translateY(-50%);border:none;background:#111827;color:#fff;width:32px;height:32px;border-radius:999px;cursor:pointer;">›</button>
        </div>`;

      // Wire simple scrolling after injection
      setTimeout(() => {
        try {
          const modal = document.querySelector('#upsell-related-modal');
          const track = modal?.querySelector('.upsell-track');
          const prev = modal?.querySelector('.upsell-nav.prev');
          const next = modal?.querySelector('.upsell-nav.next');
          const scrollBy = () => { try { track.scrollBy({ left: track.clientWidth - 80, behavior: 'smooth' }); } catch { } };
          const scrollByNeg = () => { try { track.scrollBy({ left: -1 * (track.clientWidth - 80), behavior: 'smooth' }); } catch { } };
          if (next) next.addEventListener('click', scrollBy);
          if (prev) prev.addEventListener('click', scrollByNeg);
        } catch { }
      }, 50);

      return html;
    } catch {
      return `<div style="padding:18px;">No suggestions right now.</div>`;
    }
  }

  escapeHtml(str) {
    try { return (str || '').toString().replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }[c])); } catch { return str; }
  }
  escapeAttr(str) {
    try { return (str || '').toString().replace(/["]+/g, ''); } catch { return str; }
  }

  async addToCartViaGraphQL() {
    try {
      const token = this.getStorefrontAccessToken();
      if (!token) throw new Error('Missing Storefront access token');
      const shopDomain = 'bysnaps.myshopify.com';
      const graphqlUrl = `https://${shopDomain}/api/2024-04/graphql.json`;

      // Collect inputs
      const variantNumericId = this.getVariantNumericId();
      const quantityInput = this.formEl?.querySelector('input[name="quantity"]');
      const quantity = Math.max(1, parseInt(quantityInput?.value || '1', 10));
      if (!variantNumericId) throw new Error('Variant ID not found');
      const merchandiseId = `gid://shopify/ProductVariant/${variantNumericId}`;

      // Build attributes
      const attributes = [];
      const kidName = (this.kidNameInput?.value || '').trim();
      if (kidName) attributes.push({ key: 'Kid Name', value: kidName });
      const kidAge = (this.kidAgeInput?.value || '').trim();
      if (kidAge) attributes.push({ key: 'Kid Age', value: kidAge });
      this.processed.forEach((p, i) => {
        attributes.push({ key: `Photo ${i + 1}`, value: p.azureUrl || p.previewUrl });
      });

      // Ensure cart
      let cartId = localStorage.getItem('sfy_cart_id');
      if (!cartId) {
        const createMutation = `
          mutation cartCreate($input: CartInput!) {
            cartCreate(input: $input) {
              cart { id }
              userErrors { field message }
            }
          }
        `;
        const variables = {
          input: {
            lines: [
              { quantity, merchandiseId, attributes }
            ]
          }
        };
        const res = await this.graphqlRequest(graphqlUrl, token, createMutation, variables);
        const errs = res?.data?.cartCreate?.userErrors;
        if (errs && errs.length) throw new Error(errs.map(e => e.message).join(', '));
        cartId = res?.data?.cartCreate?.cart?.id;
        if (!cartId) throw new Error('cartCreate failed');
        localStorage.setItem('sfy_cart_id', cartId);
        return true;
      } else {
        const addMutation = `
          mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
            cartLinesAdd(cartId: $cartId, lines: $lines) {
              cart { id }
              userErrors { field message }
            }
          }
        `;
        const variables = {
          cartId,
          lines: [{ quantity, merchandiseId, attributes }]
        };
        const res = await this.graphqlRequest(graphqlUrl, token, addMutation, variables);
        const errs = res?.data?.cartLinesAdd?.userErrors;
        if (errs && errs.length) throw new Error(errs.map(e => e.message).join(', '));
        return true;
      }
    } catch (e) {
      console.error('❌ addToCartViaGraphQL failed', e);
      this.showToaster('Could not add item to cart via GraphQL. Proceeding…', 'warning');
      return false;
    }
  }

  getStorefrontAccessToken() {
    try {
      let token = null;
      const tokenMeta = document.querySelector('meta[name="shopify-storefront-api-token"]');
      if (tokenMeta) token = tokenMeta.getAttribute('content');
      if (!token && window.Shopify && window.Shopify.storefrontAccessToken) token = window.Shopify.storefrontAccessToken;
      if (!token && window.theme && window.theme.storefrontAccessToken) token = window.theme.storefrontAccessToken;
      return token;
    } catch { return null; }
  }

  async graphqlRequest(url, token, query, variables) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
        'Accept': 'application/json'
      },
      body: JSON.stringify({ query: query.trim(), variables }),
      mode: 'cors',
      credentials: 'omit'
    });
    if (!res.ok) throw new Error(`GraphQL ${res.status}`);
    return await res.json();
  }

  async addToCartViaAjax() {
    try {
      if (!this.formEl) throw new Error('Form not found');
      // Build fresh FormData including our properties
      this.syncHiddenInputsSync();
      const fd = new FormData(this.formEl);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        body: fd,
        headers: { 'Accept': 'application/json' }
      });
      if (!res.ok) throw new Error(`add.js ${res.status}`);
      return true;
    } catch (e) {
      console.error('❌ addToCartViaAjax failed', e);
      return false;
    }
  }

  // Collect selected lines (variant + kid name + kid age + photos) from the open bundle accordion
  collectBundleLines(openItem) {
    try {
      if (!openItem) return [];
      const lines = Array.from(openItem.querySelectorAll('.bundle-line'));
      const result = [];
      lines.forEach((line, idx) => {
        const variantId = line.querySelector('.bundle-variant')?.value;
        if (!variantId) return; // skip unselected
        const uploader = line.querySelector('.bundle-uploader-line .kid-info-section');
        if (!uploader) return; // skip if uploader not shown yet
        const kidName = (uploader.querySelector('#kid-name-input')?.value || '').trim();
        const kidAge = (uploader.querySelector('.kid-age')?.value || '').trim();
        const photos = Array.from(uploader.querySelectorAll('.multi-photo-grid .photo-preview.multi-item'))
          .map(el => el.getAttribute('data-image-url'))
          .filter(Boolean);
        result.push({ index: idx + 1, variantId, kidName, kidAge, photos });
      });
      return result;
    } catch (e) {
      console.error('❌ collectBundleLines failed', e);
      return [];
    }
  }

  // Build AJAX API items payload where each story is a separate line item
  buildBundleItems(lines) {
    try {
      const items = [];
      lines.forEach((line) => {
        const properties = {};
        if (line.kidName) properties['Kid Name'] = line.kidName;
        if (line.kidAge) properties['Kid Age'] = line.kidAge;
        (line.photos || []).forEach((url, i) => {
          properties[`Photo ${i + 1}`] = url;
        });
        items.push({ id: line.variantId, quantity: 1, properties });
      });
      return items;
    } catch (e) {
      console.error('❌ buildBundleItems failed', e);
      return [];
    }
  }

  // Add multiple lines to cart in a single request
  async addMultipleToCartViaAjax(items) {
    try {
      if (!Array.isArray(items) || items.length === 0) return false;
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items }),
        signal: controller.signal
      });
      clearTimeout(timer);
      // Consume body to avoid pending stream blocking UI
      try { await res.clone().json().catch(() => null); } catch { }
      if (!res.ok) {
        console.error('❌ addMultipleToCartViaAjax status', res.status);
        return false;
      }
      return true;
    } catch (e) {
      console.error('❌ addMultipleToCartViaAjax failed', e);
      return false;
    }
  }

  getVariantNumericId() {
    try {
      const tryGet = (root) => {
        let el = root?.querySelector('input.product-variant-id');
        if (!el) el = root?.querySelector('input[name="id"]');
        if (el && el.value) return el.value;
        return null;
      };
      // 1) from this.formEl
      let id = tryGet(this.formEl);
      // 2) nearest form to uploader container
      if (!id && this.container) {
        const nearestForm = this.container.closest('product-info, .product-form')?.querySelector('form[data-type="add-to-cart-form"]')
          || document.querySelector('form[data-type="add-to-cart-form"]');
        id = tryGet(nearestForm);
      }
      // 3) global fallback
      if (!id) {
        id = tryGet(document);
      }
      // Normalize: if it's a GID, extract numeric tail
      if (id && id.startsWith('gid://')) {
        const parts = id.split('/');
        id = parts[parts.length - 1];
      }
      if (id && /^\d+$/.test(id)) return id;
      return null;
    } catch {
      return null;
    }
  }

  // In bundle, ensure the main form's variant id matches the selected story in the open accordion (first line)
  setBundleVariantOnForm() {
    try {
      if (!this.isBundleOpen() || !this.formEl) return;
      const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
      if (!openItem) return;
      const firstSelect = openItem.querySelector('.bundle-line .bundle-variant');
      const variantId = firstSelect?.value;
      if (!variantId) return;
      const idInput = this.formEl.querySelector('input[name="id"]');
      if (idInput) idInput.value = variantId;
    } catch (e) {
      console.error('[BundleValidation] setBundleVariantOnForm failed', e);
    }
  }

  async refreshCartBubble() {
    try {
      const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      const count = cart?.item_count ?? 0;
      const bubble = document.querySelector('.cart-count-bubble, [data-cart-count]');
      if (bubble) {
        bubble.textContent = count;
        bubble.style.display = count > 0 ? '' : 'none';
      }
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } catch { /* ignore */ }
  }
  getProductFormContainer() {
    try {
      if (!this.formEl) return null;
      // Try custom element first
      let el = this.formEl.closest('product-form');
      if (el) return el;
      // Fallback to any ancestor with class product-form
      el = this.formEl.closest('.product-form');
      if (el) return el;
      // Fallback to buttons wrapper
      return this.formEl.querySelector('.product-form__buttons')?.parentElement || null;
    } catch { return null; }
  }

  // -------- Bundle helpers ---------
  isBundleOpen() {
    try {
      const openEl = document.querySelector('.bundle-accordion details.bundle-item[open]');
      return !!openEl;
    } catch (e) {
      console.error('[BundleValidation] isBundleOpen error', e);
      return false;
    }
  }

  isOpenBundleValid() {
    try {
      const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
      if (!openItem) return false;
      const required = parseInt(openItem.getAttribute('data-size'), 10) || 1;
      // If this is the last accordion (baseSize >= 4), enforce validation on all added lines as well
      const group = openItem.parentElement;
      const allAccordions = group?.querySelectorAll('details.bundle-item');
      const isLast = allAccordions && allAccordions[allAccordions.length - 1] === openItem;
      const uploaders = openItem.querySelectorAll('.bundle-uploader-line .kid-info-section');
      if (uploaders.length < required) {
        console.error('[BundleValidation] Not enough uploaders', { required, found: uploaders.length });
        return false;
      }
      // If not last accordion, validate only up to required lines; if last, validate all existing uploaders
      const maxIndex = isLast ? uploaders.length : Math.min(required, uploaders.length);
      for (let idx = 0; idx < maxIndex; idx++) {
        const uploader = uploaders[idx];
        if (!uploader) return false; // story not selected yet / no uploader rendered
        // Validate photos
        let photosCount = 0;
        try {
          photosCount = uploader.querySelectorAll('.multi-photo-grid .photo-preview.multi-item').length;
          if (photosCount === 0) {
            // Fallback: accept previews directly under uploader or any element with data-image-url
            photosCount = uploader.querySelectorAll('.photo-preview.multi-item').length;
          }
          if (photosCount === 0) {
            photosCount = uploader.querySelectorAll('[data-image-url]').length;
          }
        } catch { }
        if (photosCount < 1) {
          console.error('[BundleValidation] No photos for line', { line: idx + 1 });
          return false;
        }
        // Validate name
        const input = uploader.querySelector('#kid-name-input');
        const nameVal = (input?.value || '').trim();
        if (nameVal.length < 2) {
          console.error('[BundleValidation] Kid name too short', { line: idx + 1, value: nameVal });
          return false;
        }
        // No language enforcement in bundle validation; only length is required
      }
      // All lines valid
      return true;
    } catch (e) {
      console.error('[BundleValidation] validity check failed', e);
      return false;
    }
  }

  // Overall validity across bundle UI:
  // - If bundle accordions exist: require at least one accordion open and that open one is valid
  // - If no bundle accordions: fall back to single form validity
  isOverallValid() {
    try {
      const hasBundle = !!document.querySelector('.bundle-accordion details.bundle-item');
      if (hasBundle) {
        const openItem = document.querySelector('.bundle-accordion details.bundle-item[open]');
        if (!openItem) return false;
        return this.isOpenBundleValid();
      }
      return this.isFormValid();
    } catch (e) {
      return false;
    }
  }

  // Helper to get story language from inputs/metafields
  getStoryLanguage() {
    try {
      // 1. Check direct input (most reliable for user selection)
      const input = this.container?.querySelector('#story-language, .story-language, [name="properties[Story Language]"]');
      if (input && input.value) {
        const val = input.value.toLowerCase().trim();
        if (val === 'arabic' || val === 'ar' || val.includes('arabic') || val.includes('عربي')) return 'Arabic';
        return 'English';
      }

      // 2. Check container dataset (from liquid)
      if (this.container?.dataset?.storyLanguage) {
        const val = this.container.dataset.storyLanguage.toLowerCase().trim();
        if (val === 'arabic' || val === 'ar') return 'Arabic';
      }

      // 3. Fallback to English/Latin if no explicit Arabic signal found
      return 'English';
    } catch (e) {
      return 'English';
    }
  }

  // Validate kid name language matches story language
  validateKidNameLanguage() {
    try {
      // Get kid name
      const kidNameInput = this.container?.querySelector('#kid-name-input') ||
        this.kidNameInput ||
        document.getElementById('kid-name-input');

      if (!kidNameInput) {
        return { valid: true, message: '' }; // No kid name input, skip validation
      }

      const kidName = kidNameInput.value;

      if (!kidName || kidName.trim() === '') {
        return { valid: true, message: '' }; // Empty name is handled by other validation
      }

      const trimmedName = kidName.trim();

      // Detect language from inputs
      const storyLanguage = this.getStoryLanguage();

      // Check for Arabic characters
      const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;
      const hasArabic = arabicPattern.test(trimmedName);

      // Check for Latin characters (English and other Latin-based languages)
      const latinPattern = /[a-zA-ZÀ-ÿ\u0100-\u017F\u0180-\u024F\u1E00-\u1EFF]/;
      const hasLatin = latinPattern.test(trimmedName);

      // Arabic story validation
      if (storyLanguage === 'Arabic') {
        // Invalid only if has Latin characters WITHOUT any Arabic
        if (hasLatin && !hasArabic) {
          return {
            valid: false,
            message: 'Kid name must be in Arabic when story language is Arabic'
          };
        }
      }
      // Non-Arabic story validation (English, French, Spanish, etc.)
      else {
        // Invalid only if has Arabic characters WITHOUT any Latin
        if (hasArabic && !hasLatin) {
          return {
            valid: false,
            message: 'Kid name must be in Latin characters (English, French, etc.) when story language is not Arabic'
          };
        }
      }

      return { valid: true, message: '' };
    } catch (e) {
      console.error('[validateKidNameLanguage] error', e);
      return { valid: true, message: '' }; // Don't block on error
    }
  }

  // COMMENTED OUT - Face API methods (Will use them later)

  // async ensureFaceApi() {
  //   if (this.faceApiLoaded) return;
  //   const loader = window.loadFaceAPI ? window.loadFaceAPI : null;
  //   if (!loader) throw new Error('Face API loader not found');
  //   const faceapi = await loader();

  //   // Load models
  //   const MODEL_URL = 'https://raw.githubusercontent.com/justadudewhohacks/face-api.js/master/weights';
  //   await Promise.all([
  //     faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
  //     faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
  //     faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
  //     faceapi.nets.ageGenderNet.loadFromUri(MODEL_URL)
  //   ]);
  //   this.faceApiLoaded = true;
  // }

  // async validateFace(imageElement) {
  //   if (!this.faceApiLoaded) {
  //     throw new Error('Face detection models not loaded. Please try again.');
  //   }
  //   try {
  //     const detections = await faceapi
  //       .detectAllFaces(imageElement, new faceapi.TinyFaceDetectorOptions())
  //       .withFaceLandmarks()
  //       .withAgeAndGender();

  //     if (detections.length === 0) {
  //       // Same messaging as previewer.js
  //       throw new Error("No face detected in the image. Please upload a photo showing your child's face more clearly.");
  //     }
  //     if (detections.length > 1) {
  //       // Same messaging as previewer.js
  //       throw new Error('Multiple faces detected. Please upload a photo with only your child in it.');
  //     }

  //     const detection = detections[0];
  //     if (detection.detection.score < 0.4) {
  //       // Same messaging as previewer.js
  //       throw new Error('Face detection confidence is low. Please try a clearer photo if possible, or this one should work fine.');
  //     }

  //     return {
  //       success: true,
  //       age: Math.round(detection.age),
  //       confidence: detection.detection.score,
  //       gender: detection.gender,
  //       faceBox: detection.detection.box
  //     };
  //   } catch (error) {
  //     // For product page: enforce strict selection like requested.
  //     // If validation-specific messages occur, surface them and reject image.
  //     throw error;
  //   }
  // }

  // async cropImageAroundFace(imageElement, faceBox, marginFactor = 0.8) {
  //   return new Promise((resolve, reject) => {
  //     try {
  //       const canvas = document.createElement('canvas');
  //       const ctx = canvas.getContext('2d');
  //       if (!ctx) throw new Error('Canvas context failed');

  //       const faceWidth = faceBox.width;
  //       const faceHeight = faceBox.height;
  //       const marginX = faceWidth * marginFactor;
  //       const marginY = faceHeight * marginFactor;

  //       let cropX = Math.max(0, faceBox.x - marginX * 0.8);
  //       let cropY = Math.max(0, faceBox.y - marginY * 1.5);
  //       let cropWidth = Math.min(imageElement.width - cropX, faceWidth + (marginX * 1.6));
  //       let cropHeight = Math.min(imageElement.height - cropY, faceHeight + (marginY * 2.3));

  //       if (cropX + cropWidth > imageElement.width) cropWidth = imageElement.width - cropX;
  //       if (cropY + cropHeight > imageElement.height) cropHeight = imageElement.height - cropY;

  //       canvas.width = cropWidth;
  //       canvas.height = cropHeight;
  //       ctx.drawImage(imageElement, cropX, cropY, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

  //       // Use PNG format for lossless compression and maximum quality (1.0)
  //       canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error('Blob failed')), 'image/png', 1.0);
  //     } catch (e) { reject(e); }
  //   });
  // }

  createImageElement(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  }

  async uploadImageToAzure(file) {
    try {
      const azureBaseUrl = 'https://snapsai.blob.core.windows.net/input/faces';
      const sasToken = 'sv=2023-01-03&spr=https%2Chttp&st=2025-06-10T18%3A29%3A47Z&se=2030-06-10T18%3A29%3A00Z&sr=c&sp=rw&sig=aW5Phdxt%2FeKteE%2BfX8e27QVWEg%2FbX2o64zMWeBAW3jc%3D';
      const timestamp = Date.now();
      const randomId = Math.random().toString(36).substring(2, 15);
      const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
      const blobName = `product_photo_${timestamp}_${randomId}.${ext}`;
      const uploadUrl = `${azureBaseUrl}/${blobName}?${sasToken}`;

      const resp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': file.type
        },
        body: file,
        mode: 'cors'
      });
      if (!resp.ok) throw new Error(`Azure upload failed: ${resp.status}`);
      return `${azureBaseUrl}/${blobName}`;
    } catch (err) {
      console.warn('⚠️ Azure upload failed, using object URL:', err);
      try { return URL.createObjectURL(file); } catch { return null; }
    }
  }


  showLoadingState(message) {
    const zone = this.uploadSection;
    if (!zone) return;
    zone.style.opacity = '0.6';
    zone.style.pointerEvents = 'none';
    // Ensure overlay positions relative to the zone
    try {
      const currentPos = (window.getComputedStyle ? getComputedStyle(zone).position : zone.style.position) || '';
      if (!currentPos || currentPos === 'static') {
        zone.style.position = 'relative';
      }
    } catch { }
    // Ensure spinner CSS exists (fallback if stylesheet not loaded)
    try {
      if (!document.getElementById('face-validation-spinner-styles')) {
        const style = document.createElement('style');
        style.id = 'face-validation-spinner-styles';
        style.textContent = `
          @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          .face-validation-loading { 
            position: absolute !important; 
            top: 0 !important; 
            left: 0 !important; 
            right: 0 !important; 
            bottom: 0 !important; 
            background: rgba(255,255,255,0.95) !important; 
            display: flex !important; 
            flex-direction: column !important; 
            align-items: center !important; 
            justify-content: center !important; 
            z-index: 9999 !important; 
            border-radius: 12px !important; 
          }
          .face-validation-loading .loading-spinner { 
            width: 40px !important; 
            height: 40px !important; 
            border: 3px solid #f3f4f6 !important; 
            border-top: 3px solid #8b5cf6 !important; 
            border-radius: 50% !important; 
            animation: spin 1s linear infinite !important; 
            margin-bottom: 1rem !important; 
            display: block !important;
          }
          .face-validation-loading p { 
            color: #8b5cf6 !important; 
            font-size: 0.875rem !important; 
            font-weight: 600 !important; 
            margin: 0 !important; 
            text-align: center !important; 
            display: block !important;
          }
        `;
        document.head.appendChild(style);
      }
    } catch { }
    let overlay = zone.querySelector('.face-validation-loading');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'face-validation-loading';
      overlay.innerHTML = `
        <div class="loading-spinner"></div>
        <p></p>
      `;
      zone.appendChild(overlay);
      try {
        overlay.style.position = 'absolute';
        overlay.style.top = '0';
        overlay.style.left = '0';
        overlay.style.right = '0';
        overlay.style.bottom = '0';
        overlay.style.background = 'rgba(255,255,255,0.95)';
        overlay.style.display = 'flex';
        overlay.style.flexDirection = 'column';
        overlay.style.alignItems = 'center';
        overlay.style.justifyContent = 'center';
        overlay.style.zIndex = '9999';
        overlay.style.borderRadius = '12px';
        overlay.style.visibility = 'visible';
        overlay.style.opacity = '1';

        const spinner = overlay.querySelector('.loading-spinner');
        if (spinner) {
          spinner.style.width = '40px';
          spinner.style.height = '40px';
          spinner.style.border = '3px solid #f3f4f6';
          spinner.style.borderTop = '3px solid #8b5cf6';
          spinner.style.borderRadius = '50%';
          spinner.style.animation = 'spin 1s linear infinite';
          spinner.style.marginBottom = '1rem';
          spinner.style.display = 'block';
          spinner.style.visibility = 'visible';
          spinner.style.opacity = '1';
        }

        const text = overlay.querySelector('p');
        if (text) {
          text.style.display = 'block';
          text.style.visibility = 'visible';
          text.style.opacity = '1';
          text.style.color = '#8b5cf6';
          text.style.fontSize = '0.875rem';
          text.style.fontWeight = '600';
          text.style.margin = '0';
          text.style.textAlign = 'center';
        }
      } catch { }
    }
    const p = overlay.querySelector('p');
    if (p) p.textContent = message;
  }

  hideLoadingState() {
    const zone = this.uploadSection;
    if (!zone) return;
    zone.style.opacity = '1';
    zone.style.pointerEvents = 'auto';
    const overlay = zone.querySelector('.face-validation-loading');
    if (overlay) overlay.remove();
  }

  scrollToKidNameField() {
    // Try to scroll to the kid name input field first
    const kidNameInput = document.getElementById('kid-name-input');
    if (kidNameInput) {
      kidNameInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Optional: Focus the field to help user see where to input
      setTimeout(() => {
        kidNameInput.focus();
      }, 500);
    } else {
      // Fallback to container if kid name field not found
      this.container?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  showToaster(message, type = 'info') {
    const existing = document.querySelector('.toaster');
    if (existing) existing.remove();
    const el = document.createElement('div');
    el.className = `toaster toaster-${type}`;
    let icon = '<i class="fas fa-info-circle"></i>';
    if (type === 'success') icon = '<i class="fas fa-check-circle"></i>';
    if (type === 'error') icon = '<i class="fas fa-exclamation-circle"></i>';
    if (type === 'warning') icon = '<i class="fas fa-exclamation-triangle"></i>';
    el.innerHTML = `
      <div class="toaster-content">
        ${icon}
        <span class="toaster-message">${message}</span>
        <button class="toaster-close" aria-label="Close" onclick="this.parentElement.parentElement.remove()">
          <i class="fas fa-times"></i>
        </button>
      </div>
    `;
    document.body.appendChild(el);
    setTimeout(() => el.classList.add('toaster-show'), 50);
    setTimeout(() => { if (el && el.parentElement) el.remove(); }, type === 'error' ? 5000 : 3000);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  try {
    // Only initialize if the container exists on page
    if (document.getElementById('product-multi-uploader')) {
      const containerEl = document.getElementById('product-multi-uploader');
      const enableCrop = (containerEl?.dataset?.enableCrop || 'true') === 'true';
      // Use maxFiles: 100
      window.productMultiUploader = new ProductMultiUploader({ maxFiles: 100, enableCrop });
    }

    // Bundle uploader logic
    const bundleAccordions = document.querySelectorAll('.bundle-accordion details.bundle-item');
    if (bundleAccordions.length > 0) {
      // Create a controller to manage guards/disable state even before any uploader clones exist
      if (!window.productMultiUploader) {
        const bundleRoot = document.querySelector('.bundle-uploader');
        if (bundleRoot) {
          // Avoid attaching duplicate global handlers which can cause double-intercepts and lockups
          window.productMultiUploader = new ProductMultiUploader({ maxFiles: 5, enableCrop: true, containerEl: bundleRoot, skipGlobal: true });
        }
      }
      bundleAccordions.forEach((item) => {
        const size = parseInt(item.getAttribute('data-size'), 10) || 1;
        let currentCount = size;
        const sectionEl = document.querySelector('.bundle-uploader');
        const sectionId = sectionEl?.getAttribute('data-section-id') || 'x';
        const linesRoot = item.querySelector(`#bundle-lines-${sectionId}-${size}`) || item.querySelector('.bundle-lines');

        const buildAndAppendLine = async (index) => {
          const ds = parseInt(item.getAttribute('data-size'), 10) || size;
          const line = document.createElement('div');
          line.className = 'bundle-line';
          line.innerHTML = `
            <label>Story #${index}</label>
            <select class="bundle-variant" style="display:none;"></select>
            <div class="bundle-combobox" aria-haspopup="listbox" aria-expanded="false">
              <div class="combo-input-wrapper" style="position:relative;">
                <input type="search" class="combo-input" placeholder="Search Story #${index}…" autocomplete="off" style="width:100%; padding:8px 36px 8px 10px; border:1px solid #e5e7eb; border-radius:6px;">
                <span class="combo-caret" style="position:absolute; right:10px; top:50%; transform:translateY(-50%); pointer-events:none; color:#6b7280;">▾</span>
              </div>
              <ul class="combo-list" role="listbox" style="display:none; position:absolute; z-index:1000; background:#fff; border:1px solid #e5e7eb; border-radius:6px; margin-top:6px; max-height:220px; overflow:auto; width:100%; box-shadow:0 4px 10px rgba(0,0,0,.08); padding:0;"></ul>
            </div>
            <div class="bundle-uploader-line" id="bundle-uploader-${ds}-${index}"></div>
          `;
          linesRoot.appendChild(line);

          const variantSelect = line.querySelector('.bundle-variant');
          const tplSelect = document.querySelector('.bundle-variants-template');
          let allOptions = [];
          if (tplSelect) {
            allOptions = Array.from(tplSelect.options)
              .filter(o => o.value)
              .map(o => ({ id: String(o.value), text: (o.textContent || '').trim(), img: o.getAttribute('data-img') || '' }));
          }
          if (!allOptions.length) {
            const variants = await (this.getVariantsAsync ? this.getVariantsAsync() : Promise.resolve(this.getVariantsFromJSON() || []));
            const formatTitle = (v) => v.public_title || v.title || v.name || v.id;
            const getImg = (v) => {
              try {
                if (v && v.featured_media && v.featured_media.preview_image && v.featured_media.preview_image.src) return v.featured_media.preview_image.src;
              } catch { }
              try { if (v && v.featured_image && (v.featured_image.src || typeof v.featured_image === 'string')) return v.featured_image.src || v.featured_image; } catch { }
              try { if (v && v.image && (v.image.src || typeof v.image === 'string')) return v.image.src || v.image; } catch { }
              return '';
            };
            allOptions = variants.map(v => ({ id: String(v.id), text: formatTitle(v), img: getImg(v) }));
          }
          variantSelect.innerHTML = ['<option value="">Select a Story</option>']
            .concat(allOptions.map(o => `<option value="${o.id}">${o.text}</option>`))
            .join('');
          const combo = line.querySelector('.bundle-combobox');
          ProductMultiUploader.prototype.initAutocompleteDropdown.call(ProductMultiUploader.prototype, combo, variantSelect, allOptions);

          variantSelect.addEventListener('change', () => {
            const selected = variantSelect.value;
            const target = line.querySelector(`#bundle-uploader-${ds}-${index}`);
            target.innerHTML = '';
            if (!selected) return;
            const tpl = document.querySelector('.bundle-uploader-template .kid-info-section');
            const clone = tpl ? tpl.cloneNode(true) : document.getElementById('product-multi-uploader')?.cloneNode(true);
            clone.id = `product-multi-uploader-${ds}-${index}`;
            target.appendChild(clone);
            const enableCrop = (clone?.dataset?.enableCrop || 'true') === 'true';
            // Use maxFiles: 100
            new ProductMultiUploader({ maxFiles: 100, enableCrop, containerEl: clone });
          });
        };

        const renderLines = async (count) => {
          if (!linesRoot) return;
          linesRoot.innerHTML = '';
          for (let i = 1; i <= count; i++) {
            await buildAndAppendLine(i);
          }
          ensureAddControl();
          ensureRemoveControl();
          updateBundleSummaryTitle(count);
          try { window.productMultiUploader?.updateButtonsState(); } catch { }
        };

        const ensureAddControl = () => {
          try {
            // Only allow adding extra stories on the LAST accordion and when base size >= 4
            const baseSize = parseInt(item.getAttribute('data-size'), 10) || 1;
            const allAccordions = item.parentElement?.querySelectorAll('details.bundle-item');
            const isLast = allAccordions && allAccordions[allAccordions.length - 1] === item;
            if (!(isLast && baseSize >= 4)) {
              // Remove if previously added
              const existing = item.querySelector('.bundle-add-control');
              if (existing) existing.remove();
              return;
            }
            let control = item.querySelector('.bundle-add-control');
            if (!control) {
              control = document.createElement('div');
              control.className = 'bundle-add-control';
              control.style.display = 'flex';
              control.style.justifyContent = 'center';
              control.style.marginTop = '12px';
              control.innerHTML = `
                <button type="button" class="bundle-add-line" style="display:inline-flex;align-items:center;gap:8px;padding:10px 14px;border:1px dashed #9ca3af;border-radius:8px;background:#f9fafb;color:#111827;font-weight:700;">
                  <i class="fas fa-plus"></i>
                  <span>+ Add New Story</span>
                </button>
              `;
              linesRoot.parentElement.insertBefore(control, linesRoot.nextSibling);
              const btn = control.querySelector('.bundle-add-line');
              btn.addEventListener('click', async () => {
                try {
                  currentCount += 1;
                  await buildAndAppendLine(currentCount);
                  try { window.productMultiUploader?.updateButtonsState(); } catch { }
                  const last = linesRoot.querySelector('.bundle-line:last-child');
                  if (last && last.scrollIntoView) last.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  ensureRemoveControl();
                  updateBundleSummaryTitle(currentCount);
                } catch { }
              });
            }
          } catch { }
        };

        const ensureRemoveControl = () => {
          try {
            // Minimum required lines equals the original data-size for this accordion
            const minRequired = parseInt(item.getAttribute('data-size'), 10) || 1;
            // Remove any existing remove buttons
            Array.from(item.querySelectorAll('.bundle-remove-line')).forEach((b) => b.remove());
            const lastLine = linesRoot.querySelector('.bundle-line:last-child');
            if (!lastLine) return;
            if ((currentCount || 0) <= minRequired) return; // keep minimum
            // Ensure the row can position an absolute button on far right
            try { if (!lastLine.style.position || lastLine.style.position === 'static') lastLine.style.position = 'relative'; } catch { }
            // Add a small X remove control aligned to far right
            const label = lastLine.querySelector('label');
            if (!label) return;
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'bundle-remove-line';
            btn.setAttribute('aria-label', 'Remove story');
            btn.innerHTML = '<i class="fas fa-times"></i>';
            btn.style.position = 'absolute';
            btn.style.top = '6px';
            btn.style.right = '0';
            btn.style.border = 'none';
            btn.style.background = 'transparent';
            btn.style.cursor = 'pointer';
            btn.style.color = '#dc2626';
            btn.style.fontSize = '16px';
            lastLine.appendChild(btn);
            btn.addEventListener('click', async () => {
              try {
                const minReq = parseInt(item.getAttribute('data-size'), 10) || 1;
                if (currentCount <= minReq) return;
                currentCount -= 1;
                await renderLines(currentCount);
                updateBundleSummaryTitle(currentCount);
              } catch { }
            });
          } catch { }
        };

        const updateBundleSummaryTitle = (count) => {
          try {
            const titleEl = item.querySelector('.bundle-title');
            if (!titleEl) return;
            const n = Math.max(1, parseInt(count, 10) || 1);
            titleEl.textContent = `${n} ${n === 1 ? 'Story' : 'Stories'}`;
          } catch { }
        };

        item.addEventListener('toggle', () => {
          if (item.open) {
            // Close siblings to keep only one accordion open
            try {
              const group = item.closest('.bundle-accordion');
              if (group) {
                const siblings = group.querySelectorAll('details.bundle-item');
                siblings.forEach((d) => { if (d !== item) d.removeAttribute('open'); });
              }
            } catch { }
            currentCount = Math.max(currentCount, parseInt(item.getAttribute('data-size'), 10) || currentCount);
            renderLines(currentCount);
          }
        });
        if (item.open) {
          renderLines(currentCount);
        }
      });
    }
  } catch (e) { console.error('❌ Failed to init ProductMultiUploader:', e); }
});


// Utilities for searchable selects in bundle
ProductMultiUploader.prototype.normalizeText = function (str) {
  try {
    return (str || '')
      .toString()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase();
  } catch { return (str || '').toString().toLowerCase(); }
};

ProductMultiUploader.prototype.renderSelectOptions = function (selectEl, allOptions, query) {
  const q = this.normalizeText(query || '');
  const filtered = !q ? allOptions : allOptions.filter(o => this.normalizeText(o.text).includes(q));
  const html = ['<option value="">Select a Story</option>']
    .concat(filtered.map(o => `<option value="${o.id}">${o.text}</option>`))
    .join('');
  selectEl.innerHTML = html;
};

ProductMultiUploader.prototype.makeSelectSearchable = function (selectEl, allOptions) {
  try {
    const wrapper = document.createElement('div');
    wrapper.className = 'searchable-select';
    const search = document.createElement('input');
    search.type = 'search';
    search.placeholder = 'Search stories…';
    search.className = 'searchable-select-input';
    search.style.width = '100%';
    search.style.padding = '8px 10px';
    search.style.marginBottom = '6px';
    search.style.border = '1px solid #e5e7eb';
    search.style.borderRadius = '6px';
    const parent = selectEl.parentElement;
    parent.insertBefore(wrapper, selectEl);
    wrapper.appendChild(search);
    wrapper.appendChild(selectEl);

    let lastQuery = '';
    const onInput = () => {
      const q = search.value || '';
      if (q === lastQuery) return;
      lastQuery = q;
      this.renderSelectOptions(selectEl, allOptions, q);
    };
    search.addEventListener('input', onInput);
  } catch (e) { console.warn('makeSelectSearchable failed', e); }
};

// Enable typeahead on native select: user types to jump/filter within options
ProductMultiUploader.prototype.makeSelectTypeahead = function (selectEl, allOptions) {
  try {
    let buffer = '';
    let lastTime = 0;
    const resetDelay = 700; // ms

    const focusSelect = () => { try { selectEl.focus(); } catch { } };
    selectEl.addEventListener('keydown', (e) => {
      const now = Date.now();
      if (now - lastTime > resetDelay) buffer = '';
      lastTime = now;

      const key = e.key;
      if (key.length === 1) {
        buffer += key;
      } else if (key === 'Backspace') {
        buffer = buffer.slice(0, -1);
      } else {
        return; // ignore other control keys
      }

      const q = this.normalizeText(buffer);
      const idx = allOptions.findIndex(o => this.normalizeText(o.text).includes(q));
      if (idx >= 0) {
        const value = allOptions[idx].id;
        const opt = Array.from(selectEl.options).find(o => o.value === value);
        if (opt) {
          selectEl.value = value;
          // fire change so downstream logic (uploader insertion) runs immediately
          selectEl.dispatchEvent(new Event('change'));
        }
      }
      // Prevent page scroll when typing space
      if (key === ' ') e.preventDefault();
    });

    // Improve accessibility: focus select on click of label area
    const label = selectEl.previousElementSibling;
    if (label && label.tagName.toLowerCase() === 'label') {
      label.style.cursor = 'text';
      label.addEventListener('click', focusSelect);
    }
  } catch (e) { console.warn('makeSelectTypeahead failed', e); }
};

ProductMultiUploader.prototype.renderNativeSelectWithSearch = function (selectEl, allOptions, placeholder) {
  // Render options
  selectEl.innerHTML = ['<option value="">Select a Story</option>']
    .concat(allOptions.map(o => `<option value="${o.id}">${o.text}</option>`))
    .join('');

  // Add a search field above the select but without moving the select
  const header = document.createElement('div');
  header.style.margin = '6px 0';
  const input = document.createElement('input');
  input.type = 'search';
  input.placeholder = placeholder || 'Search…';
  input.style.width = '100%';
  input.style.padding = '8px 10px';
  input.style.border = '1px solid #e5e7eb';
  input.style.borderRadius = '6px';
  selectEl.parentElement.insertBefore(header, selectEl);
  header.appendChild(input);

  const original = allOptions.slice(0);
  const update = () => {
    const q = this.normalizeText(input.value || '');
    const filtered = !q ? original : original.filter(o => this.normalizeText(o.text).includes(q));
    selectEl.innerHTML = ['<option value="">Select a Story</option>']
      .concat(filtered.map(o => `<option value="${o.id}">${o.text}</option>`))
      .join('');
  };
  input.addEventListener('input', update);
};

ProductMultiUploader.prototype.getVariantsFromJSON = function () {
  try {
    const holder = document.querySelector('.bundle-uploader');
    if (holder && holder.dataset && holder.dataset.variants) {
      const arr = JSON.parse(holder.dataset.variants);
      if (Array.isArray(arr) && arr.length) return arr;
    }
    const el = document.querySelector('.bundle-variants-json');
    if (el) {
      const arr = JSON.parse(el.textContent || '[]');
      if (Array.isArray(arr) && arr.length) return arr;
    }
    // Fallback: scrape from native variant select on page
    const domSelect = document.querySelector('form[data-type="add-to-cart-form"] select[name="id"]');
    if (domSelect) {
      const opts = Array.from(domSelect.options).map(o => ({ id: String(o.value), title: (o.textContent || '').trim() }));
      return opts.map(o => ({ id: o.id, title: o.title, public_title: o.title }));
    }
    return [];
  } catch { return null; }
};

// Async helper: try multiple sources and, if needed, request product JSON endpoint
ProductMultiUploader.prototype.getVariantsAsync = async function () {
  try {
    const fromData = this.getVariantsFromJSON();
    if (fromData && fromData.length) return fromData;
  } catch { }
  // Try /products/{handle}.js
  try {
    const handle = (window?.ShopifyAnalytics?.meta?.product?.handle) || (window?.location?.pathname.split('/products/')[1] || '').split('/')[0];
    if (handle) {
      const res = await fetch(`/products/${handle}.js`, { headers: { 'Accept': 'application/json' } });
      if (res.ok) {
        const data = await res.json();
        if (data && Array.isArray(data.variants)) return data.variants;
      }
    }
  } catch { }
  // Fallback to native select scrape
  try {
    const domSelect = document.querySelector('form[data-type="add-to-cart-form"] select[name="id"]');
    if (domSelect) {
      const opts = Array.from(domSelect.options).map(o => ({ id: String(o.value), title: (o.textContent || '').trim() }));
      return opts.map(o => ({ id: o.id, title: o.title, public_title: o.title }));
    }
  } catch { }
  return [];
};

// Build simple autocomplete dropdown tied to a hidden native select
ProductMultiUploader.prototype.initAutocompleteDropdown = function (comboRoot, selectEl, allOptions) {
  try {
    const input = comboRoot.querySelector('.combo-input');
    const list = comboRoot.querySelector('.combo-list');
    // Ensure the list has an explicit width equal to input
    const syncWidth = () => {
      try { list.style.width = input.getBoundingClientRect().width + 'px'; } catch { }
    };
    syncWidth();
    window.addEventListener('resize', syncWidth);
    const open = () => { list.style.display = 'block'; comboRoot.setAttribute('aria-expanded', 'true'); };
    const close = () => { list.style.display = 'none'; comboRoot.setAttribute('aria-expanded', 'false'); };
    const norm = (t) => this.normalizeText(t);

    const renderList = (query) => {
      const q = norm(query || '');
      const filtered = !q ? allOptions : allOptions.filter(o => norm(o.text).includes(q));
      // Get per-option image from the template select if available
      const getImgFromOptions = (id) => {
        const found = allOptions.find(o => String(o.id) === String(id));
        return (found && found.img) ? found.img : '';
      };
      list.innerHTML = filtered.map(o => {
        const img = getImgFromOptions(o.id);
        const thumb = img ? `<img src="${img}" alt="" style="width:45px;height:45px;object-fit:cover;border-radius:6px;margin-right:10px;">` : '';
        return `<li role="option" data-id="${o.id}" style="padding:8px 10px; cursor:pointer; display:flex; align-items:center;">${thumb}<span>${o.text}</span></li>`;
      }).join('');
    };

    const commit = (id) => {
      selectEl.value = id || '';
      selectEl.dispatchEvent(new Event('change'));
      const chosen = allOptions.find(o => o.id === id);
      input.value = chosen ? chosen.text : '';
      close();
    };

    // Initial state
    input.value = '';
    renderList('');

    input.addEventListener('focus', () => { renderList(''); open(); });
    input.addEventListener('input', () => { renderList(input.value); open(); });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { close(); }
      if (e.key === 'Enter') {
        const first = list.querySelector('li');
        if (first) commit(first.getAttribute('data-id'));
        e.preventDefault();
      }
    });
    document.addEventListener('click', (e) => { if (!comboRoot.contains(e.target)) close(); });
    list.addEventListener('click', (e) => {
      const li = e.target.closest('li[role="option"]');
      if (!li) return;
      commit(li.getAttribute('data-id'));
    });
  } catch (e) { console.warn('initAutocompleteDropdown failed', e); }
};

